import type { ReactNode } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { comoSeGuardara, formatearMonto } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { totalUfDeLaConstruccion, type FormularioDeConstruccion } from "./construcciones";

type ClaveDelFormulario = keyof FormularioDeConstruccion;

interface CamposDeConstruccionProps {
  /**
   * Prefijo de los `id` de los controles.
   *
   * EL FORMULARIO EN LÍNEA Y EL MODAL DE EDICIÓN CONVIVEN EN EL DOM. Con el
   * modal abierto, el formulario de la lista puede seguir montado detrás, así
   * que sin prefijos distintos habría dos controles con el mismo `id` y
   * `getByLabelText` (o cualquier tecnología asistiva) encontraría dos.
   */
  idPrefijo: string;
  formulario: FormularioDeConstruccion;
  ocupada: boolean;
  escribir: (cambio: Partial<FormularioDeConstruccion>) => void;
  /**
   * Los datos sobre los que el encargado dejó una nota en terreno: llevan un
   * punto ámbar en el rótulo y un halo en el campo. La nota NO cambia el valor.
   */
  conNota?: ReadonlySet<ClaveDelFormulario>;
  /** Los obligatorios: se resaltan en ámbar mientras estén vacíos. */
  requeridos?: ClaveDelFormulario[];
}

/** El rótulo de un grupo de campos: versalitas chicas, más callado que el dato. */
export function RotuloDeGrupo({ children, aparte }: { children: ReactNode; aparte?: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-ink-body text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
        {children}
      </span>
      {aparte ? <span className="text-ink-hint text-caption">{aparte}</span> : null}
    </div>
  );
}

interface CampoProps {
  id: string;
  etiqueta: string;
  unidad?: string;
  placeholder: string;
  inputMode?: "numeric" | "decimal";
  valor: string;
  clase?: string;
  conNota: boolean;
  faltante: boolean;
  disabled: boolean;
  /** Una lectura bajo el campo: cómo se va a guardar lo escrito. */
  lectura?: string | null;
  /** Lo que sigue a la lectura: " por m²" en construcciones, nada en maquinaria. */
  sufijoDeLectura?: string;
  onChange: (valor: string) => void;
}

/**
 * Un campo del formulario de una fila con cuadro: rótulo, entrada y lectura.
 *
 * LO COMPARTE `CamposDeMaquina`: las dos pestañas dibujan el mismo campo del
 * diseño, y con dos copias el día que cambie su alto o su halo de nota habría
 * que acordarse de las dos.
 */
export function Campo({
  id,
  etiqueta,
  unidad,
  placeholder,
  inputMode,
  valor,
  clase,
  conNota,
  faltante,
  disabled,
  lectura,
  sufijoDeLectura = " por m²",
  onChange,
}: CampoProps) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", clase)}>
      <Label htmlFor={id} className="mb-0 items-center gap-1.5">
        {etiqueta}
        {unidad ? <span className="text-ink-hint font-semibold">{unidad}</span> : null}
        {conNota ? (
          <span
            title="Tiene nota de terreno"
            className="bg-warning-dot size-[0.4375rem] rounded-full"
          >
            <span className="sr-only">Tiene nota de terreno</span>
          </span>
        ) : null}
      </Label>
      <Input
        id={id}
        inputMode={inputMode}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          conNota && "ring-warning-dot/35 ring-2",
          faltante && "border-warning-dot/60",
        )}
      />
      {lectura ? (
        <p className="text-ink-hint text-caption">
          Se guardará {lectura}
          {sufijoDeLectura}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Identificación: grupo, tipo de edificación, año, superficie y clase.
 *
 * `nota_terreno` —la columna de texto libre que existía antes de las notas por
 * dato— ya no tiene campo propio en el diseño, pero la fila que la trae
 * cargada la sigue mostrando acá: ocultarla dejaría un dato guardado sin
 * forma de verlo ni de corregirlo.
 */
export function GrupoDeIdentificacion({
  idPrefijo,
  formulario,
  ocupada,
  escribir,
  conNota,
  requeridos = [],
}: CamposDeConstruccionProps) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave],
    conNota: conNota?.has(clave) ?? false,
    faltante: requeridos.includes(clave) && formulario[clave].trim() === "",
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Campo
        id={`${idPrefijo}-grupo`}
        etiqueta="Grupo / categoría"
        placeholder="Ej.: Casa patronal y oficinas"
        clase="col-span-2"
        {...campo("grupo")}
      />
      <Campo
        id={`${idPrefijo}-tipo`}
        etiqueta="Tipo de edificación"
        placeholder="Ej.: Casa patronal"
        clase="col-span-2"
        {...campo("tipo_edificacion")}
      />
      <Campo
        id={`${idPrefijo}-anio`}
        etiqueta="Año"
        inputMode="numeric"
        placeholder="Ej.: 1960"
        {...campo("anio")}
      />
      <Campo
        id={`${idPrefijo}-superficie`}
        etiqueta="Superficie"
        unidad="m²"
        inputMode="decimal"
        placeholder="Ej.: 540,00"
        {...campo("superficie_m2")}
      />
      {/* TEXTO LIBRE Y NO UN SELECTOR de un maestro: el prototipo usa DOS
          vocabularios —"F-1" acá y "Media"/"Buena" en el cuadro— y sembrar el
          catálogo exige saber cuál es. Lo decide quien tasa. */}
      <Campo
        id={`${idPrefijo}-clase`}
        etiqueta="Clase / calidad"
        placeholder="Ej.: F-1"
        {...campo("clase")}
      />
      {formulario.nota_terreno.trim() === "" ? null : (
        <Campo
          id={`${idPrefijo}-nota`}
          etiqueta="Nota de terreno (anterior)"
          placeholder=""
          clase="col-span-2 lg:col-span-4"
          {...campo("nota_terreno")}
        />
      )}
    </div>
  );
}

/**
 * Valorización: vida útil total y residual, los dos valores unitarios en
 * UF/m² y el ajuste comercial.
 *
 * LA LECTURA ("Se guardará UF 16 por m²") ES LO QUE HACE VISIBLE LA
 * AMBIGÜEDAD DEL PUNTO: "16.000" acá son dieciséis mil, pero quien copia de
 * otra pantalla escribe "16.00" queriendo decir dieciséis.
 */
export function GrupoDeValorizacion({
  idPrefijo,
  formulario,
  ocupada,
  escribir,
  conNota,
}: CamposDeConstruccionProps) {
  const campo = (clave: ClaveDelFormulario) => ({
    valor: formulario[clave],
    conNota: conNota?.has(clave) ?? false,
    faltante: false,
    disabled: ocupada,
    onChange: (valor: string) => escribir({ [clave]: valor }),
  });

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Campo
        id={`${idPrefijo}-vida-total`}
        etiqueta="Vida útil total"
        unidad="años"
        inputMode="numeric"
        placeholder="Ej.: 80"
        {...campo("vida_util_total")}
      />
      <Campo
        id={`${idPrefijo}-vida-residual`}
        etiqueta="Vida útil residual"
        unidad="años"
        inputMode="numeric"
        placeholder="Ej.: 14"
        {...campo("vida_util_residual")}
      />
      <Campo
        id={`${idPrefijo}-reposicion`}
        etiqueta="Valor reposición"
        unidad="UF/m²"
        inputMode="decimal"
        placeholder="Ej.: 16,00"
        lectura={comoSeGuardara(formulario.valor_reposicion_uf_m2, "UF")}
        {...campo("valor_reposicion_uf_m2")}
      />
      <Campo
        id={`${idPrefijo}-residual`}
        etiqueta="Valor residual"
        unidad="UF/m²"
        inputMode="decimal"
        placeholder="Ej.: 0,80"
        lectura={comoSeGuardara(formulario.valor_residual_uf_m2, "UF")}
        {...campo("valor_residual_uf_m2")}
      />
      <Campo
        id={`${idPrefijo}-ajuste`}
        etiqueta="Ajuste comercial"
        unidad="%"
        inputMode="decimal"
        placeholder="Ej.: -10"
        {...campo("ajuste_comercial_pct")}
      />
    </div>
  );
}

/**
 * El "Valor estimado": lo que el servidor va a calcular con lo escrito.
 *
 * La cuenta es la del servidor (`totalUfDeLaConstruccion`, su gemelo) y NO la
 * del prototipo: la leyenda describe ESTA fórmula. Y el SIGNO se explica acá,
 * donde se ve el resultado: quien viene del papel del prototipo espera que un
 * ajuste negativo sume.
 */
export function ValorEstimado({ formulario }: { formulario: FormularioDeConstruccion }) {
  const total = totalUfDeLaConstruccion(formulario);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-3.5 py-2.5",
        total === null ? "bg-superficie-sutil border-divisor" : "bg-success-soft border-success-dot/40",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-ink-muted text-[0.65625rem] font-extrabold tracking-[0.06em] uppercase">
          Valor estimado
        </div>
        <p className="text-ink-muted mt-0.5 text-[0.6875rem] font-semibold text-pretty">
          {total === null
            ? "Se calcula al completar superficie y valor residual. Un ajuste negativo descuenta."
            : "Superficie × valor residual × (1 + ajuste). Un ajuste negativo descuenta."}
        </p>
      </div>
      <span
        className={cn("flex-none font-mono text-lg font-bold", total === null ? "text-ink-ghost" : "text-ink")}
      >
        {total === null ? "—" : `${formatearMonto(total, null)} UF`}
      </span>
    </div>
  );
}
